/**
 * GqlClient — tiny typed GraphQL-over-fetch wrapper for platform mode.
 *
 * Deliberately NOT Apollo: a handful of queries and a refresh mutation don't
 * justify a cache layer. Responsibilities:
 *   - headers: `Authorization: Bearer`, `spaces_client`, `spaces_tenant`
 *   - JSON / GraphQL errors → typed `VisitorsApiError {code,message,retryable}`
 *   - auto refresh-and-retry ONCE on UNAUTHORIZED (HTTP 401 or extensions.code)
 *   - when the session cannot be refreshed, hand it to SessionRecoveryService
 *     (one toast, one redirect to sign-in) before throwing UNAUTHORIZED
 *   - network / CORS failures classified as `NETWORK` (retryable)
 *
 * WHY THE CLIENT TRIGGERS RECOVERY ITSELF (O4, 3 Oct 2026). Recovery used to
 * run only when the thrown UNAUTHORIZED reached the global error handler, so
 * any caller that caught errors — the host search, the type options — ate the
 * expiry: overnight, reception's host search showed "No employees match that
 * search" and never sent anybody back to sign in.
 */

import { Injectable, inject } from '@angular/core';

import { VisitorsAuthService, SPACES_CLIENT_ID } from './auth.service';
import { RDX_VISITORS_ENV } from './env';
import { SessionRecoveryService } from './session-recovery.service';

/** The codes a server uses for "who are you?" — both mean the session is gone. */
const SESSION_CODES = new Set(['UNAUTHORIZED', 'UNAUTHENTICATED']);

/** The shape every platform-mode failure travels in. */
export interface ApiError {
  code: string;
  message: string;
  retryable: boolean;
}

export class VisitorsApiError extends Error implements ApiError {
  override readonly name = 'VisitorsApiError';

  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    /** Structured payload from the server error's GraphQL extensions —
     *  e.g. VISIT_VALIDATION carries conflicts/emailConflicts/fieldErrors. */
    readonly extensions?: Record<string, unknown>,
  ) {
    super(message);
  }
}

export function isApiError(e: unknown): e is VisitorsApiError {
  return e instanceof VisitorsApiError;
}

/**
 * True for a fetch aborted via AbortController. Such errors are a deliberate
 * client cancellation (component teardown) — they must be swallowed silently,
 * never surfaced as a NETWORK failure or toast.
 */
export function isAbortError(e: unknown): boolean {
  return (
    !!e &&
    typeof e === 'object' &&
    (e as { name?: string }).name === 'AbortError'
  );
}

interface GraphQLErrorShape {
  message?: string;
  extensions?: { code?: string } & Record<string, unknown>;
}

interface GraphQLResponse<T> {
  data?: T | null;
  errors?: GraphQLErrorShape[];
}

/** Error codes the user can't fix by retrying. */
const NON_RETRYABLE_CODES = new Set(['UNAUTHORIZED', 'FORBIDDEN', 'BAD_USER_INPUT']);

@Injectable({ providedIn: 'root' })
export class GqlClient {
  private readonly auth = inject(VisitorsAuthService);
  private readonly recovery = inject(SessionRecoveryService);

  /**
   * Execute a GraphQL operation; resolves with `data`, throws VisitorsApiError.
   * Pass an `AbortSignal` to cancel an in-flight request; an aborted call
   * rejects with a raw AbortError the caller is expected to swallow (see
   * isAbortError).
   */
  async gql<T>(
    query: string,
    variables?: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<T> {
    return this.run<T>(query, variables, false, signal);
  }

  private headers(): Record<string, string> {
    const headers: Record<string, string> = {
      'content-type': 'application/json',
      spaces_client: SPACES_CLIENT_ID,
    };
    const token = this.auth.accessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const tenant = this.auth.tenant();
    if (tenant) headers['spaces_tenant'] = tenant;
    return headers;
  }

  private async run<T>(
    query: string,
    variables: Record<string, unknown> | undefined,
    retried: boolean,
    signal?: AbortSignal,
  ): Promise<T> {
    let res: Response;
    try {
      res = await fetch(RDX_VISITORS_ENV.API_URL, {
        method: 'POST',
        credentials: 'include',
        headers: this.headers(),
        body: JSON.stringify({ query, variables }),
        signal,
      });
    } catch (e) {
      // A deliberate cancel — rethrow raw so the caller can swallow it. NEVER
      // dress it up as a NETWORK error (that would surface a spurious toast).
      if (isAbortError(e)) throw e as Error;
      // fetch() rejects on DNS/connection failures AND blocked CORS — both
      // mean "we never got a server answer", classified as NETWORK.
      throw new VisitorsApiError(
        'NETWORK',
        $localize`:@@visitors-error-network:Could not reach the Visitors API — check your connection and try again.`,
        true,
      );
    }

    // A bare HTTP 401 (no GraphQL body) — refresh once, then retry once.
    if (res.status === 401 && !retried && (await this.auth.refresh())) {
      return this.run<T>(query, variables, true, signal);
    }

    let json: GraphQLResponse<T>;
    try {
      json = (await res.json()) as GraphQLResponse<T>;
    } catch {
      if (res.status === 401) throw this.sessionExpired();
      throw new VisitorsApiError(
        res.ok ? 'BAD_RESPONSE' : 'SERVER',
        $localize`:@@visitors-error-unreadable-response:The Visitors API returned an unreadable response (HTTP ${res.status}:status:).`,
        res.status >= 500,
      );
    }

    const errors = json.errors ?? [];
    if (errors.length > 0) {
      const first = errors[0];
      const code = first?.extensions?.code ?? (res.status === 403 ? 'FORBIDDEN' : 'SERVER');
      const sessionGone = res.status === 401 || SESSION_CODES.has(code);

      if (sessionGone && !retried && res.status !== 401 && (await this.auth.refresh())) {
        return this.run<T>(query, variables, true, signal);
      }
      // Refreshed and still refused, or the refresh itself failed: the
      // session is over, whichever caller asked.
      if (sessionGone) throw this.sessionExpired(first?.message);

      throw new VisitorsApiError(
        code,
        first?.message ?? $localize`:@@visitors-error-rejected:The Visitors API rejected the request.`,
        !NON_RETRYABLE_CODES.has(code),
        first?.extensions,
      );
    }

    if (res.status === 401) throw this.sessionExpired();
    if (!res.ok) {
      throw new VisitorsApiError(
        'SERVER',
        $localize`:@@visitors-error-server-failed:The Visitors API failed (HTTP ${res.status}:status:).`,
        true,
      );
    }

    if (json.data === undefined || json.data === null) {
      throw new VisitorsApiError(
        'BAD_RESPONSE',
        $localize`:@@visitors-error-empty-response:The Visitors API returned an empty response.`,
        false,
      );
    }
    return json.data;
  }

  /** Send the user back to sign in (once), and fail the call as UNAUTHORIZED. */
  private sessionExpired(message?: string): VisitorsApiError {
    this.recovery.handleExpiry();
    return new VisitorsApiError(
      'UNAUTHORIZED',
      message ?? $localize`:@@visitors-error-session-expired:Your session expired.`,
      false,
    );
  }
}
